import type { ClaimView, VouchView, Anchor } from "../api/client";
import { RungBadge } from "./Rung";
import { DidLabel } from "./DidLabel";
import { NameBasisBadge } from "./NameBasisBadge";

const TYPE_LABEL: Record<string, string> = { peer: "Peer", affiliated: "Affiliated", light_issuer: "Organisation", issuer: "Issuer" };

function initials(name: string) {
  return name.split(/\s+/).filter((w) => /^[A-Z]/.test(w)).map((w) => w[0]).join("").slice(0, 3) || name.slice(0, 2).toUpperCase();
}

export function VouchRow({ v, issuerName }: { v: VouchView; issuerName?: string }) {
  if (v.decision === "declined") return null;    // declines never appear publicly
  // A vouch made on behalf of an issuer: the person, speaking for the issuer.
  if (v.delegation) {
    const anchored = v.delegation.status === "anchor";
    return (
      <div className={`vouch${v.withdrawn ? " withdrawn" : ""}`} data-testid="vouch" data-type="authorised_attester" data-delegation={v.delegation.status}>
        <span className={`typebadge ${anchored ? "t-issuer" : "t-affiliated"}`}>{anchored ? "Issuer" : TYPE_LABEL.affiliated}</span>
        <span className="who">{v.attester_name ?? "Authorised attester"} · on behalf of {issuerName ?? "the issuer"}</span>
        <span className="meta">{v.basis_label ?? v.basis}{v.role ? ` · ${v.role}` : ""}{anchored ? "" : ` · counts as affiliated${v.delegation.reason ? ` (${v.delegation.reason})` : ""}`}</span>
      </div>
    );
  }
  const who = v.attester_type === "peer" ? "Vouched by a peer" : v.attester_name ?? v.attester_label ?? TYPE_LABEL[v.attester_type];
  return (
    <div className={`vouch${v.withdrawn ? " withdrawn" : ""}`} data-testid="vouch" data-type={v.attester_type}>
      <span className={`typebadge t-${v.attester_type}`}>{TYPE_LABEL[v.attester_type]}</span>
      <span className="who">{who}</span>
      <span className="meta">
        {v.basis_label ?? v.basis}{v.relationship ? ` · ${v.relationship}` : ""}
        {v.proof_type ? " · organisation proof: domain + second person" : ""}
        {v.attester_type === "peer" ? <> · <DidLabel did={v.attester_did} name={v.display_name ?? null} /></> : null}
      </span>
    </div>
  );
}

/**
 * "Anchored by <issuer> · <how its name is backed> · via <name>, authorised attester".
 * With no basis from the rail, the rail's own label.
 */
export function AnchorText({ a }: { a: Anchor }) {
  if (!a.issuer_name_basis || !a.issuer_name) return <>{a.label ?? `Anchored by ${a.issuer_name ?? a.issuer_did}`}</>;
  return (<>Anchored by {a.issuer_name} · <NameBasisBadge basis={a.issuer_name_basis} domain={a.issuer_domain} />
    {a.kind === "delegated" ? ` · via ${a.attester_name ?? "an authorised attester"}, authorised attester` : " · anchored directly"}</>);
}

export function AnchorLine({ a }: { a: Anchor }) {
  return (
    <div className={`anchorline${a.status === "demoted" ? " demoted" : ""}`} data-testid="anchor" data-status={a.status}>
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1 15 8 8 15 1 8Z" fill="currentColor" /></svg>
      <span><AnchorText a={a} />
        {a.status === "demoted" ? " (delegation revoked)" : ""}</span>
    </div>
  );
}

export function StampCard({ view, compact = false, children }: { view: ClaimView; compact?: boolean; children?: React.ReactNode }) {
  const c = view.claim;
  const fields = Object.entries(c.fields ?? {});
  const active = view.anchors.filter((a) => a.status === "active");
  const issuerNames = new Map<string, string>(view.anchors.filter((a) => a.issuer_name).map((a) => [a.issuer_did, a.issuer_name!]));
  if (view.issuer) issuerNames.set(view.issuer.did, view.issuer.name);
  return (
    <article className="card stamp" data-testid="stamp" aria-label="Claim">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <span className="label">{c.claim_type.replace(/_/g, " ")}{view.issuer
          ? <span data-testid="stamp-issuer"> · names {view.issuer.kind === "light" ? view.issuer.domain ?? view.issuer.name : view.issuer.name} <NameBasisBadge basis={view.issuer.name_basis} domain={view.issuer.domain} /></span>
          : c.issuer_name ? ` · ${c.issuer_name}` : ""}</span>
        <RungBadge rung={view.rung} />
      </div>
      <div className="headline">{c.description}</div>
      <div className="claimed-by hint">Claimed by <DidLabel did={view.holder_did} name={view.holder_display_name ?? null} /></div>
      {fields.length > 0 && <div className="fields">{fields.map(([k, v]) => <span key={k}>{k.replace(/_/g, " ")}: <b>{String(v)}</b></span>)}</div>}
      {c.tags?.length ? <div className="tags">{c.tags.map((t) => <span className="tag" key={t}>{t}</span>)}</div> : null}
      {!compact && (
        <>
          <h2>Vouches</h2>
          {view.vouches.filter((v) => v.decision !== "declined").length === 0
            ? <p className="note">No vouches yet. Self-claimed.</p>
            : <div className="stack">{view.vouches.map((v) => <VouchRow key={v.vouch_id} v={v} issuerName={issuerNames.get(v.delegation?.issuer ?? v.on_behalf_of ?? "")} />)}</div>}
          {view.anchors.map((a) => <AnchorLine key={a.anchor_id} a={a} />)}
        </>
      )}
      {active.length > 0 && (
        <div className="issuer-slot" data-testid="issuer-slot">
          <span className="mark" aria-hidden="true">{initials(active[0].issuer_name ?? "Issuer")}</span>
          <span>{active[0].issuer_name} <NameBasisBadge basis={active[0].issuer_name_basis} domain={active[0].issuer_domain} />{active[0].kind === "delegated" ? ` · via ${active[0].attester_name}, ${active[0].role ?? "authorised attester"}` : " · anchored directly"}</span>
        </div>
      )}
      {children}
    </article>
  );
}
